// src/pages/OltConfig.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./OLTConfig.css";
import { authFetch } from "../utils/authFetch";
import {
  Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

export default function OltConfig() {
  const navigate = useNavigate();
  const [olts, setOlts] = useState([]);

  const [form, setForm] = useState({
    id: null,
    nombre: "",
    host: "",
    username: "",
    password: "",
  });

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error'|'info', msg: '' }

  // El nodo (la localidad) al que pertenece esta OLT. Sin esto la app no
  // sabe qué router la atiende ni de dónde son sus clientes.
  const [nodos, setNodos] = useState([]);
  const [nodoId, setNodoId] = useState("");
  const [nodoNuevo, setNodoNuevo] = useState("");

  // Una localidad nueva necesita saber qué router la administra. Sin este
  // dato el nodo nace con `mikrotik_id` en NULL y después no hay forma de
  // saber qué equipo atiende a sus clientes.
  const [routers, setRouters] = useState([]);
  const [routerNodo, setRouterNodo] = useState("");

  // ---------- Cargar lista de OLTs ----------
  const loadOlts = async () => {
    try {
      const res = await authFetch("/api/olt-config/");
      if (!res.ok) return;

      const data = await res.json();
      if (data.status === "OK" && Array.isArray(data.olts)) {
        setOlts(data.olts);
      } else {
        setOlts([]);
      }
    } catch (err) {
      console.error("Error cargando OLTs:", err);
    }
  };

  const loadNodos = async () => {
    try {
      const res = await authFetch("/api/nodos/listar");
      const data = await res.json();
      if (data.ok) setNodos(data.nodos);
    } catch (err) {
      console.error("Error cargando nodos:", err);
    }
  };

  const loadRouters = async () => {
    try {
      const res = await authFetch("/api/mikrotik/list");
      const data = await res.json();
      setRouters(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error cargando routers:", err);
    }
  };

  useEffect(() => {
    loadOlts();
    loadNodos();
    loadRouters();
  }, []);

  // ---------- Manejo de inputs ----------
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const cleanForm = () => {
    setForm({
      id: null,
      nombre: "",
      host: "",
      username: "",
      password: "",
    });
    setStatus(null);
  };

  // ---------- Probar conexión ----------
  const handleTest = async () => {
    setTesting(true);
    setStatus(null);

    try {
      const res = await authFetch("/api/olt-config/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: form.host,
          username: form.username,
          password: form.password,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === "OK") {
        setStatus({ type: "success", msg: data.detalle || "Conexión exitosa a la OLT." });
      } else {
        setStatus({
          type: "error",
          msg: data.detalle || "No se pudo conectar a la OLT.",
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        msg: "Error probando conexión.",
      });
    } finally {
      setTesting(false);
    }
  };

  // ---------- Guardar / actualizar ----------
  const handleSave = async () => {
    setSaving(true);
    setStatus(null);

    try {
      const res = await authFetch("/api/olt-config/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: form.id,
          nombre: form.nombre,
          host: form.host,
          username: form.username,
          password: form.password,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === "OK") {
        setStatus({
          type: "success",
          msg: data.detalle || "Configuración guardada correctamente.",
        });

        const oltId = data.id ?? form.id;

        // Ligar la OLT a su localidad. Va después de guardarla porque el
        // nodo necesita su id, y solo si se pidió: editar una OLT ya
        // registrada no debe reasignarle el nodo sin querer.
        const nombreNodo = nodoNuevo.trim();
        if (oltId && (nombreNodo || nodoId)) {
          const nodo = nodos.find((n) => String(n.id) === String(nodoId));
          const resNodo = await authFetch("/api/nodos/nuevo", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              nombre: nombreNodo || nodo?.nombre,
              olt_id: oltId,
              // Solo se manda al crear una localidad nueva: en una que ya
              // existe, el backend conserva el router que tuviera.
              mikrotik_id: nombreNodo ? routerNodo || null : null,
            }),
          });

          const datosNodo = await resNodo.json().catch(() => ({}));
          if (!datosNodo.ok) {
            setStatus({
              type: "error",
              msg: `La OLT se guardó, pero el nodo no: ${datosNodo.mensaje || "error desconocido"}`,
            });
          }

          setNodoNuevo("");
          setRouterNodo("");
          loadNodos();
        }

        // si backend regresa id, lo usamos para mantener edición
        setForm((prev) => ({
          ...prev,
          id: oltId,
          password: "", // por seguridad
        }));

        loadOlts();
      } else {
        setStatus({
          type: "error",
          msg: data.detalle || "No se pudo guardar la configuración.",
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        msg: "Error guardando la configuración.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ---------- Seleccionar OLT de la tabla ----------
  const handleSelectOlt = (olt) => {
    setForm({
      id: olt.id,
      nombre: olt.nombre || "",
      host: olt.host || olt.ip_conexion || "",
      username: olt.username || olt.usuario || "",
      password: "",
    });
    setStatus(null);
  };

  return (
    <div className="olt-page">
      <EncabezadoPagina
        icono={Iconos.olt}
        titulo="Configuración de OLT"
        resumen="Registra una OLT y prueba la conexión en tiempo real."
        acciones={
          <Boton
            type="button"
            variante="secundario"
            icono={Iconos.agregar}
            onClick={cleanForm}
            disabled={testing || saving}
          >
            Nueva conexión
          </Boton>
        }
      />

      {/* CARD PRINCIPAL */}
      <div className="olt-card">

        <div className="olt-grid">
          <div className="field">
            {/* Esta pantalla registra una OLT. Aquí solo se dice a qué
                localidad pertenece: de ahí salen el router que la atiende
                y el nodo al que pertenecerán sus clientes. */}
            <label>Localidad a la que pertenece</label>
            <select
              value={nodoNuevo ? "__nuevo__" : nodoId}
              onChange={(e) => {
                if (e.target.value === "__nuevo__") {
                  setNodoId("");
                  setNodoNuevo(" ");
                } else {
                  setNodoNuevo("");
                  setNodoId(e.target.value);
                }
              }}
            >
              <option value="">Sin cambiar</option>
              {nodos.map((n) => (
                <option key={n.id} value={n.id}>{n.nombre}</option>
              ))}
              <option value="__nuevo__">Dar de alta una localidad nueva…</option>
            </select>
            {nodoNuevo !== "" && (
              <input
                autoFocus
                placeholder="Nombre de la localidad (Yuriria)"
                value={nodoNuevo.trim()}
                onChange={(e) => setNodoNuevo(e.target.value || " ")}
              />
            )}
          </div>

          {/* Solo al dar de alta una localidad: una que ya existe conserva
              el router que tenga. Se pregunta aquí porque es el dato del
              del que dependen los clientes de esa localidad. */}
          {nodoNuevo !== "" && (
            <div className="field">
              <label>Router que la administra</label>
              <select
                value={routerNodo}
                onChange={(e) => setRouterNodo(e.target.value)}
              >
                <option value="">Elige un MikroTik…</option>
                {routers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre} — {r.ip}
                  </option>
                ))}
              </select>
              {!routerNodo && (
                <small className="field-nota">
                  Sin router no se pueden crear los usuarios PPPoE de esa
                  localidad.
                </small>
              )}
            </div>
          )}

          <div className="field">
            <label>Nombre</label>
            <input
              name="nombre"
              value={form.nombre}
              onChange={onChange}
              placeholder="Ej. Huawei MA5800-X7"
            />
          </div>

          <div className="field">
            <label>IP Conexión</label>
            <input
              name="host"
              value={form.host}
              onChange={onChange}
              placeholder="Ej. 10.4.1.2"
            />
          </div>

          <div className="field">
            <label>Usuario</label>
            <input
              name="username"
              value={form.username}
              onChange={onChange}
              placeholder="Usuario OLT"
            />
          </div>

          <div className="field">
            <label>Contraseña</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={onChange}
              placeholder="Contraseña OLT"
            />
          </div>
        </div>

        {status && (
          <div
            className={
              "olt-alert " +
              (status.type === "success"
                ? "ok"
                : status.type === "info"
                ? "info"
                : "error")
            }
          >
            {status.msg}
          </div>
        )}

        <div className="olt-actions">
          <Boton
            type="button"
            variante="secundario"
            icono={Iconos.cerrar}
            onClick={cleanForm}
            disabled={testing || saving}
          >
            Limpiar
          </Boton>

          <div className="olt-actions-right">
            <Boton
              type="button"
              variante="secundario"
              icono={Iconos.senal}
              cargando={testing}
              disabled={saving}
              onClick={handleTest}
            >
              Probar conexión
            </Boton>

            <Boton
              type="button"
              icono={Iconos.confirmar}
              cargando={saving}
              onClick={handleSave}
            >
              Guardar conexión
            </Boton>
          </div>
        </div>
      </div>

      {/* TABLA OLTs GUARDADAS */}
      <div className="olt-table-card">
        <div className="olt-table-header">
          <h3>
            <Iconos.olt size={16} strokeWidth={1.8} />
            OLTs guardadas
          </h3>
          <Insignia variante="neutra" texto={`${olts.length} registradas`} />
        </div>

        <table className="olt-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>IP</th>
              <th>Usuario</th>
              <th>Estado</th>
              <th>Última conexión OK</th>
              <th>Detalle</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {olts.length === 0 && (
              <tr>
                <td colSpan="7" className="no-data">
                  No hay OLTs guardadas.
                </td>
              </tr>
            )}

            {olts.map((o) => (
              <tr
                key={o.id}
                onClick={() => handleSelectOlt(o)}
                className="row-clickable"
              >
                <td>{o.nombre}</td>
                <td>{o.host || o.ip_conexion || o.ip || "-"}</td>
                <td>{o.username || o.usuario || "-"}</td>
                <td>
                  {(() => {
                    const estado = String(o.ultimo_estado || o.estado || "").toLowerCase();
                    if (estado === "online") {
                      return <Insignia variante="activo" icono={Iconos.enLinea} texto="En línea" />;
                    }
                    if (estado === "offline") {
                      return <Insignia variante="cortado" icono={Iconos.fueraDeLinea} texto="Fuera de línea" />;
                    }
                    // Nunca se ha probado la conexión: no es lo mismo que
                    // estar caída, y antes se veía casi igual.
                    return <Insignia variante="neutra" icono={Iconos.duda} texto="Sin probar" />;
                  })()}
                </td>
                <td>
                  {o.ultima_conexion_ok
                    ? new Date(o.ultima_conexion_ok).toLocaleString()
                    : "-"}
                </td>
                <td className="mensaje-cell">
                  {o.ultimo_mensaje || o.detalle || "-"}
                </td>
                {/* El clic en el renglón carga la OLT en el formulario de
                    arriba, así que abrir el chasis necesita su propio
                    botón y detener la propagación. */}
                <td onClick={(e) => e.stopPropagation()}>
                  <Boton
                    variante="secundario"
                    icono={Iconos.olt}
                    onClick={() => navigate(`/olts/${o.id}`)}
                  >
                    Abrir
                  </Boton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

